/**
 * Alshallal Pastry Company HR System - Import Page
 * Phase 13: Bulk Employee Onboarding via Excel
 * 
 * Features:
 * - Profile selection
 * - File upload (XLSX/CSV)
 * - Sheet selection for XLSX files
 * - Validation preview
 * - Execute import
 * - Credentials CSV download
 * - Batch history
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  Loader2,
  Users,
  FileText,
  History,
  Play,
  RefreshCw,
  Eye,
  ShieldAlert,
  Database,
  ShieldCheck,
  Wallet,
  FileArchive
} from 'lucide-react';
import SalaryLoaderTab from './SalaryLoaderTab';
import DocumentUploadTab from './DocumentUploadTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ImportPage() {
  const { t, language, isRTL } = useLanguage();
  const { token, user } = useAuth();

  // State
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState('');
  const [file, setFile] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [availableSheets, setAvailableSheets] = useState([]);
  
  const [validationResult, setValidationResult] = useState(null);
  const [executionResult, setExecutionResult] = useState(null);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [approvals, setApprovals] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [dryRunning, setDryRunning] = useState(false);
  const [error, setError] = useState('');
  
  const [activeTab, setActiveTab] = useState('upload');
  const [bulkDocEnabled, setBulkDocEnabled] = useState(false);

  // Load profiles on mount
  useEffect(() => {
    loadProfiles();
    loadBatches();
    loadApprovals();
    // feature flag: hide the Document Upload tab unless enabled in this environment
    fetch(`${API_URL}/api/import/documents/enabled`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((d) => setBulkDocEnabled(!!d.enabled))
      .catch(() => setBulkDocEnabled(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProfiles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/import/profiles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProfiles(data.profiles || []);
        // Auto-select employee-onboarding if available
        const onboarding = data.profiles?.find(p => p.id === 'employee-onboarding');
        if (onboarding) {
          setSelectedProfile(onboarding.id);
        }
      }
    } catch (err) {
      console.error('Failed to load profiles:', err);
    }
  };

  const loadBatches = async () => {
    try {
      const response = await fetch(`${API_URL}/api/import/batches?page_size=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBatches(data.batches || []);
      }
    } catch (err) {
      console.error('Failed to load batches:', err);
    }
  };

  const loadApprovals = async () => {
    try {
      const response = await fetch(`${API_URL}/api/import/approvals?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setApprovals(data.approvals || []);
      }
    } catch (err) {
      console.error('Failed to load approvals:', err);
    }
  };

  const downloadSignoff = async (approval) => {
    try {
      const response = await fetch(`${API_URL}/api/import/approvals/${approval.id}/signoff?fmt=md`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        setError(t('signoffDownloadFailed', 'Could not download the sign-off sheet.'));
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safe = (approval.file_name || 'import').replace(/\W+/g, '_');
      link.download = `signoff_${safe}_${(approval.file_hash || '').slice(0, 8)}.md`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || t('signoffDownloadFailed', 'Could not download the sign-off sheet.'));
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setValidationResult(null);
      setExecutionResult(null);
      setDryRunResult(null);
      setSelectedSheet('');
      setAvailableSheets([]);
      setError('');
    }
  };

  const handleValidate = async () => {
    if (!file || !selectedProfile) {
      setError(t('pleaseSelectFileAndProfile', 'Please select a file and profile'));
      return;
    }

    setValidating(true);
    setError('');
    setValidationResult(null);
    setExecutionResult(null);
    setDryRunResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      let url = `${API_URL}/api/import/validate?profile_id=${selectedProfile}`;
      if (selectedSheet) {
        url += `&sheet_name=${encodeURIComponent(selectedSheet)}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || data.detail?.error || 'Validation failed');
        return;
      }

      setValidationResult(data);
      
      // Update available sheets if XLSX
      if (data.available_sheets?.length > 0) {
        setAvailableSheets(data.available_sheets);
        if (!selectedSheet) {
          setSelectedSheet(data.sheet_name || '');
        }
      }

    } catch (err) {
      setError(err.message || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  const handleExecute = async () => {
    if (!validationResult?.validation_id) {
      setError(t('noValidationId', 'No validation ID - please validate first'));
      return;
    }

    setExecuting(true);
    setError('');

    try {
      // Approved-file-only lock: execute requires the approval token issued by
      // the passing dry-run. Block here if it's missing so the user re-runs it.
      const approvalToken = dryRunResult?.approval_token;
      if (!approvalToken) {
        setError(t('approvalRequired', 'Run a dry-run that passes to approve this file before executing.'));
        setExecuting(false);
        return;
      }

      const response = await fetch(
        `${API_URL}/api/import/execute?validation_id=${validationResult.validation_id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Import-Approval-Token': approvalToken
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || data.detail?.error || 'Execution failed');
        return;
      }

      setExecutionResult(data);
      loadBatches(); // Refresh batch list
      loadApprovals(); // Refresh approvals audit trail

    } catch (err) {
      setError(err.message || 'Execution failed');
    } finally {
      setExecuting(false);
    }
  };

  const handleDryRun = async () => {
    if (!validationResult?.validation_id) {
      setError(t('noValidationId', 'No validation ID - please validate first'));
      return;
    }

    setDryRunning(true);
    setError('');
    setDryRunResult(null);

    try {
      const response = await fetch(
        `${API_URL}/api/import/dry-run?validation_id=${validationResult.validation_id}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || data.detail?.error || 'Dry-run failed');
        return;
      }

      setDryRunResult(data);
    } catch (err) {
      setError(err.message || 'Dry-run failed');
    } finally {
      setDryRunning(false);
    }
  };

  const handleDownloadCredentials = async (batchId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/import/batches/${batchId}/credentials`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Download failed');
        return;
      }

      // Download as file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `credentials_${batchId.slice(0, 8)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (err) {
      setError(err.message || 'Download failed');
    }
  };

  const handleReset = () => {
    setFile(null);
    setSelectedSheet('');
    setAvailableSheets([]);
    setValidationResult(null);
    setExecutionResult(null);
    setDryRunResult(null);
    setError('');
    // Reset file input
    const fileInput = document.getElementById('file-upload');
    if (fileInput) fileInput.value = '';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      validated: { variant: 'default', icon: CheckCircle, color: 'text-blue-500' },
      executed: { variant: 'default', icon: CheckCircle, color: 'text-green-500' },
      failed: { variant: 'destructive', icon: XCircle, color: 'text-red-500' },
      executing: { variant: 'secondary', icon: Loader2, color: 'text-yellow-500' },
    };
    const config = statusConfig[status] || statusConfig.validated;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className={`h-3 w-3 ${config.color} ${status === 'executing' ? 'animate-spin' : ''}`} />
        {status}
      </Badge>
    );
  };

  return (
    <div className={`p-6 space-y-6 ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Users className="h-6 w-6" />
            {t('bulkEmployeeImport', 'Bulk Employee Import')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('importDescription', 'Import employees from Excel or CSV files')}
          </p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="upload" className="gap-2">
            <Upload className="h-4 w-4" />
            {t('uploadImport', 'Upload & Import')}
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="h-4 w-4" />
            {t('importHistory', 'Import History')}
          </TabsTrigger>
          <TabsTrigger value="approvals" className="gap-2" data-testid="approvals-tab-trigger">
            <ShieldCheck className="h-4 w-4" />
            {t('approvalsAudit', 'Approvals Audit')}
          </TabsTrigger>
          <TabsTrigger value="salary" className="gap-2" data-testid="salary-tab-trigger">
            <Wallet className="h-4 w-4" />
            {t('bulkSalary', 'Bulk Salary')}
          </TabsTrigger>
          {bulkDocEnabled && (
            <TabsTrigger value="documents" className="gap-2" data-testid="documents-tab-trigger">
              <FileArchive className="h-4 w-4" />
              {t('bulkDocuments', 'Document Upload')}
            </TabsTrigger>
          )}
        </TabsList>

        {/* Upload & Import Tab */}
        <TabsContent value="upload" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Upload Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5" />
                  {t('uploadFile', 'Upload File')}
                </CardTitle>
                <CardDescription>
                  {t('supportedFormats', 'Supported formats: .xlsx, .csv')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Profile Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t('importProfile', 'Import Profile')}
                  </label>
                  <Select value={selectedProfile} onValueChange={setSelectedProfile}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('selectProfile', 'Select profile')} />
                    </SelectTrigger>
                    <SelectContent>
                      {profiles.map((profile) => (
                        <SelectItem key={profile.id} value={profile.id}>
                          {language === 'ar' && profile.name_ar ? profile.name_ar : profile.name}
                          {profile.is_sample && ' (Sample)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* File Upload */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t('selectFile', 'Select File')}
                  </label>
                  <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                    <input
                      id="file-upload"
                      type="file"
                      accept=".xlsx,.csv"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">
                        {file ? file.name : t('clickToUpload', 'Click to upload or drag and drop')}
                      </p>
                      {file && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      )}
                    </label>
                  </div>
                </div>

                {/* Sheet Selection (for XLSX) */}
                {availableSheets.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      {t('selectSheet', 'Select Sheet')}
                    </label>
                    <Select value={selectedSheet} onValueChange={setSelectedSheet}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('selectSheet', 'Select sheet')} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableSheets.map((sheet) => (
                          <SelectItem key={sheet} value={sheet}>
                            {sheet}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t('sheetHint', 'Change sheet and re-validate to use different data')}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4">
                  <Button
                    onClick={handleValidate}
                    disabled={!file || !selectedProfile || validating}
                    className="flex-1"
                  >
                    {validating ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('validating', 'Validating...')}</>
                    ) : (
                      <><CheckCircle className="h-4 w-4 mr-2" /> {t('validateFile', 'Validate File')}</>
                    )}
                  </Button>
                  <Button variant="outline" onClick={handleReset}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Validation Result */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {t('validationResult', 'Validation Result')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!validationResult && !executionResult ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>{t('uploadToValidate', 'Upload a file to see validation results')}</p>
                  </div>
                ) : executionResult ? (
                  // Execution Result
                  <div className="space-y-4">
                    <Alert variant="default" className="border-green-500 bg-green-50 dark:bg-green-900/20">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <AlertDescription className="text-green-700 dark:text-green-300">
                        {t('importComplete', 'Import completed successfully!')}
                      </AlertDescription>
                    </Alert>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-lg text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {executionResult.execution_summary?.created || 0}
                        </div>
                        <div className="text-xs text-green-600">{t('created', 'Created')}</div>
                      </div>
                      <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {executionResult.execution_summary?.matched_updated || 0}
                        </div>
                        <div className="text-xs text-blue-600">{t('updated', 'Updated')}</div>
                      </div>
                      <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg text-center">
                        <div className="text-2xl font-bold text-gray-600">
                          {executionResult.execution_summary?.matched_no_change || 0}
                        </div>
                        <div className="text-xs text-gray-600">{t('noChange', 'No Change')}</div>
                      </div>
                      <div className="bg-red-100 dark:bg-red-900/30 p-3 rounded-lg text-center">
                        <div className="text-2xl font-bold text-red-600">
                          {executionResult.execution_summary?.failed || 0}
                        </div>
                        <div className="text-xs text-red-600">{t('failed', 'Failed')}</div>
                      </div>
                    </div>

                    {executionResult.credentials_csv_available && (
                      <Button
                        onClick={() => handleDownloadCredentials(executionResult.id)}
                        className="w-full"
                        variant="outline"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        {t('downloadCredentials', 'Download Credentials CSV')}
                      </Button>
                    )}
                  </div>
                ) : (
                  // Validation Result
                  <div className="space-y-4">
                    {/* Summary */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-muted p-3 rounded-lg text-center">
                        <div className="text-2xl font-bold">{validationResult.total_rows}</div>
                        <div className="text-xs text-muted-foreground">{t('totalRows', 'Total Rows')}</div>
                      </div>
                      <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-lg text-center">
                        <div className="text-2xl font-bold text-green-600">{validationResult.valid_rows}</div>
                        <div className="text-xs text-green-600">{t('valid', 'Valid')}</div>
                      </div>
                      <div className="bg-red-100 dark:bg-red-900/30 p-3 rounded-lg text-center">
                        <div className="text-2xl font-bold text-red-600">{validationResult.invalid_rows}</div>
                        <div className="text-xs text-red-600">{t('invalid', 'Invalid')}</div>
                      </div>
                    </div>

                    {/* File Info */}
                    <div className="text-sm space-y-1">
                      <p><span className="text-muted-foreground">{t('file', 'File')}:</span> {validationResult.file_name}</p>
                      {validationResult.sheet_name && (
                        <p><span className="text-muted-foreground">{t('sheet', 'Sheet')}:</span> {validationResult.sheet_name}</p>
                      )}
                      <p><span className="text-muted-foreground">{t('profile', 'Profile')}:</span> {validationResult.profile_name}</p>
                    </div>

                    {/* Blocking Errors */}
                    {validationResult.blocking_errors?.length > 0 && (
                      <Alert variant="destructive">
                        <XCircle className="h-4 w-4" />
                        <AlertDescription>
                          <strong>{t('blockingErrors', 'Blocking Errors')}:</strong>
                          <ul className="list-disc list-inside mt-1">
                            {validationResult.blocking_errors.map((err, idx) => (
                              <li key={idx}>{err}</li>
                            ))}
                          </ul>
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* Row Errors Preview */}
                    {validationResult.row_errors?.length > 0 && (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-muted px-3 py-2 text-sm font-medium">
                          {t('rowErrors', 'Row Errors')} ({validationResult.row_errors.length})
                        </div>
                        <div className="max-h-32 overflow-y-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-16">{t('row', 'Row')}</TableHead>
                                <TableHead>{t('field', 'Field')}</TableHead>
                                <TableHead>{t('error', 'Error')}</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {validationResult.row_errors.slice(0, 10).map((err, idx) => (
                                <TableRow key={idx}>
                                  <TableCell className="font-mono">{err.row}</TableCell>
                                  <TableCell>{err.field}</TableCell>
                                  <TableCell className="text-red-600">{err.error}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}

                    {/* Validation Warnings (non-blocking, e.g. dropped emails) */}
                    {validationResult.row_warnings?.length > 0 && (
                      <div className="border border-amber-300 rounded-lg overflow-hidden" data-testid="validation-warnings">
                        <div className="bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm font-medium text-amber-700 dark:text-amber-300">
                          {t('warnings', 'Warnings')} ({validationResult.row_warnings.length}) — {t('rowsKept', 'rows kept, not rejected')}
                        </div>
                        <div className="max-h-32 overflow-y-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-16">{t('row', 'Row')}</TableHead>
                                <TableHead>{t('field', 'Field')}</TableHead>
                                <TableHead>{t('note', 'Note')}</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {validationResult.row_warnings.slice(0, 20).map((w, idx) => (
                                <TableRow key={idx}>
                                  <TableCell className="font-mono">{w.row}</TableCell>
                                  <TableCell>{w.field}</TableCell>
                                  <TableCell className="text-amber-600">{w.error}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}

                    {/* Dry-Run Preview button (the gate before Execute) */}
                    <Button
                      onClick={handleDryRun}
                      disabled={!validationResult.validation_id || dryRunning}
                      className="w-full"
                      data-testid="run-dry-run-btn"
                    >
                      {dryRunning ? (
                        <><Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('runningDryRun', 'Running dry-run...')}</>
                      ) : (
                        <><Eye className="h-4 w-4 mr-2" /> {t('runDryRun', 'Run Dry-Run Preview')}</>
                      )}
                    </Button>
                    <p className="text-xs text-center text-muted-foreground">
                      {t('dryRunHint', 'Preview the match/upsert outcome below. Nothing is written until you Execute.')}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Dry-Run Preview (full width) — the gate before Execute */}
          {dryRunResult && !executionResult && (
            <Card data-testid="dry-run-report">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  {t('dryRunPreview', 'Dry-Run Preview')}
                  <Badge variant="outline" className="ml-2">{t('noDataWritten', 'no data written')}</Badge>
                </CardTitle>
                <CardDescription>
                  {t('upsertSummary', 'Match priority Iqama → National ID → Employee #. Non-identity fields overwrite; identity fields written only via the matched key. Idempotent, no deletes.')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Summary grid */}
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  {[
                    { label: t('total', 'Total'), value: dryRunResult.summary?.total_rows, cls: 'bg-muted' },
                    { label: t('willCreate', 'Create'), value: dryRunResult.summary?.will_create, cls: 'bg-green-100 dark:bg-green-900/30 text-green-600' },
                    { label: t('willUpdate', 'Update'), value: dryRunResult.summary?.will_update, cls: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600' },
                    { label: t('noChange', 'No Change'), value: dryRunResult.summary?.will_no_change, cls: 'bg-gray-100 dark:bg-gray-800 text-gray-600' },
                    { label: t('willFail', 'Fail'), value: dryRunResult.summary?.will_fail, cls: 'bg-red-100 dark:bg-red-900/30 text-red-600' },
                    { label: t('blocked', 'Blocked'), value: dryRunResult.summary?.blocked_by_flag, cls: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600' },
                  ].map((c, i) => (
                    <div key={i} className={`p-3 rounded-lg text-center ${c.cls}`} data-testid={`dry-run-stat-${i}`}>
                      <div className="text-2xl font-bold">{c.value ?? 0}</div>
                      <div className="text-xs">{c.label}</div>
                    </div>
                  ))}
                </div>

                {/* Execute readiness */}
                <Alert
                  variant={dryRunResult.can_execute ? 'default' : 'destructive'}
                  className={dryRunResult.can_execute ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : ''}
                  data-testid="dry-run-readiness"
                >
                  {dryRunResult.can_execute
                    ? <CheckCircle className="h-4 w-4 text-green-500" />
                    : <ShieldAlert className="h-4 w-4" />}
                  <AlertDescription>
                    {dryRunResult.can_execute
                      ? t('readyToExecute', 'No blocking issues — ready to execute.')
                      : t('blockedFromExecute', 'Blocking issues must be resolved before Execute (see flags below).')}
                  </AlertDescription>
                </Alert>

                {/* Flag counts */}
                {dryRunResult.summary?.flag_count_by_type && Object.keys(dryRunResult.summary.flag_count_by_type).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(dryRunResult.summary.flag_count_by_type).map(([type, count]) => (
                      <Badge key={type} variant="secondary" data-testid={`flag-count-${type}`}>
                        {type}: {count}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Flag line items (row + field only, NO identity values) */}
                {dryRunResult.flags?.length > 0 && (
                  <div className="border rounded-lg overflow-hidden" data-testid="dry-run-flags">
                    <div className="bg-muted px-3 py-2 text-sm font-medium">
                      {t('flagDetail', 'Flag Detail')} ({dryRunResult.flags.length}) — {t('noValuesShown', 'row & field only, never values')}
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-16">{t('row', 'Row')}</TableHead>
                            <TableHead>{t('severity', 'Severity')}</TableHead>
                            <TableHead>{t('type', 'Type')}</TableHead>
                            <TableHead>{t('field', 'Field')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dryRunResult.flags.map((f, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-mono">{f.row_number}</TableCell>
                              <TableCell>
                                <Badge variant={f.severity === 'blocking' ? 'destructive' : 'secondary'}>
                                  {f.severity}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-mono text-xs">{f.type}</TableCell>
                              <TableCell>{f.field || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Execute (gated by the dry-run result) */}
                <Button
                  onClick={handleExecute}
                  disabled={!dryRunResult.can_execute || executing}
                  className="w-full"
                  variant={dryRunResult.can_execute ? 'default' : 'secondary'}
                  data-testid="execute-import-btn"
                >
                  {executing ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('executing', 'Executing...')}</>
                  ) : (
                    <><Play className="h-4 w-4 mr-2" /> {t('executeImport', 'Execute Import')}</>
                  )}
                </Button>
                {!dryRunResult.can_execute && (
                  <p className="text-xs text-center text-muted-foreground">
                    {t('resolveBlocking', 'Resolve blocking flags, re-validate, then re-run the dry-run before executing.')}
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{t('importHistory', 'Import History')}</CardTitle>
              <Button variant="outline" size="sm" onClick={loadBatches}>
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('refresh', 'Refresh')}
              </Button>
            </CardHeader>
            <CardContent>
              {batches.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>{t('noImportHistory', 'No import history yet')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('file', 'File')}</TableHead>
                        <TableHead>{t('profile', 'Profile')}</TableHead>
                        <TableHead>{t('status', 'Status')}</TableHead>
                        <TableHead className="text-center">{t('rows', 'Rows')}</TableHead>
                        <TableHead className="text-center">{t('result', 'Result')}</TableHead>
                        <TableHead>{t('date', 'Date')}</TableHead>
                        <TableHead>{t('actions', 'Actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {batches.map((batch) => (
                        <TableRow key={batch.id}>
                          <TableCell className="font-medium">
                            {batch.file_name}
                            {batch.sheet_name && (
                              <span className="text-xs text-muted-foreground block">
                                Sheet: {batch.sheet_name}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>{batch.profile_name}</TableCell>
                          <TableCell>{getStatusBadge(batch.status)}</TableCell>
                          <TableCell className="text-center">
                            {batch.total_rows}
                          </TableCell>
                          <TableCell className="text-center">
                            {batch.execution_summary ? (
                              <div className="flex items-center justify-center gap-2 text-xs">
                                <span className="text-green-600">+{batch.execution_summary.created}</span>
                                <span className="text-blue-600">~{batch.execution_summary.matched_updated}</span>
                                {batch.execution_summary.failed > 0 && (
                                  <span className="text-red-600">-{batch.execution_summary.failed}</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {batch.executed_at 
                              ? new Date(batch.executed_at).toLocaleDateString()
                              : batch.validated_at 
                                ? new Date(batch.validated_at).toLocaleDateString()
                                : '-'
                            }
                          </TableCell>
                          <TableCell>
                            {batch.credentials_csv_available && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadCredentials(batch.id)}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Approvals Audit Tab */}
        <TabsContent value="approvals">
          <Card data-testid="approvals-audit-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5" />
                  {t('approvalsAudit', 'Approvals Audit')}
                </CardTitle>
                <CardDescription>
                  {t('approvalsAuditHint', 'Who approved which file (by SHA-256), when, and whether it was executed.')}
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={loadApprovals} data-testid="refresh-approvals-btn">
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('refresh', 'Refresh')}
              </Button>
            </CardHeader>
            <CardContent>
              {approvals.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground" data-testid="approvals-empty">
                  <ShieldCheck className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>{t('noApprovals', 'No approvals yet. A passing dry-run records an approval here.')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="approvals-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('file', 'File')}</TableHead>
                        <TableHead>{t('fileSha', 'File SHA-256')}</TableHead>
                        <TableHead>{t('approvedBy', 'Approved by')}</TableHead>
                        <TableHead>{t('approvedAt', 'Approved at')}</TableHead>
                        <TableHead className="text-center">{t('status', 'Status')}</TableHead>
                        <TableHead>{t('executedBy', 'Executed by')}</TableHead>
                        <TableHead className="text-right">{t('actions', 'Actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {approvals.map((a) => (
                        <TableRow key={a.id} data-testid={`approval-row-${a.id}`}>
                          <TableCell className="font-medium">{a.file_name}</TableCell>
                          <TableCell className="font-mono text-xs" title={a.file_hash}>
                            {a.file_hash ? `${a.file_hash.slice(0, 10)}…${a.file_hash.slice(-6)}` : '-'}
                          </TableCell>
                          <TableCell className="text-sm">{a.issued_by_email || '-'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {a.issued_at ? new Date(a.issued_at).toLocaleString() : '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            {a.consumed ? (
                              <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30" data-testid={`approval-status-${a.id}`}>
                                {t('executed', 'Executed')}
                              </Badge>
                            ) : (
                              <Badge variant="outline" data-testid={`approval-status-${a.id}`}>
                                {t('pendingExecution', 'Pending')}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {a.consumed_by_email ? (
                              <>
                                {a.consumed_by_email}
                                <span className="block text-xs">
                                  {a.consumed_at ? new Date(a.consumed_at).toLocaleString() : ''}
                                </span>
                              </>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadSignoff(a)}
                              data-testid={`download-signoff-${a.id}`}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              {t('signoffSheet', 'Sign-off')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bulk Basic-Salary Loader Tab */}
        <TabsContent value="salary" className="space-y-4">
          <SalaryLoaderTab />
        </TabsContent>

        {bulkDocEnabled && (
          <TabsContent value="documents" className="space-y-4">
            <DocumentUploadTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
